import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import { fetchAdminIntegrationSyncLogs, fetchAdminIntegrationSyncItems } from '../../../services/Http/AdminApi';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminSyncMonitor = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [period, setPeriod] = useState('30d');
  const [selectedSync, setSelectedSync] = useState(null);
  const [syncItems, setSyncItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [itemsError, setItemsError] = useState('');
  const logsRequest = useRef(0);
  const itemsRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++logsRequest.current;
    itemsRequest.current += 1;
    setSelectedSync(null);
    setSyncItems([]);
    setItemsLoading(false);
    setItemsError('');
    setLoading(true);
    setLoadError('');
    try {
      const resp = await fetchAdminIntegrationSyncLogs({ status, period, per_page: 50 });
      if (request !== logsRequest.current) return;
      setRows(resp?.data || []);
    } catch (error) {
      if (request !== logsRequest.current) return;
      setRows([]);
      setLoadError('Failed to load sync logs');
      toast.error('Failed to load sync logs');
    } finally {
      if (request === logsRequest.current) setLoading(false);
    }
  }, [status, period]);

  useEffect(() => {
    load();
    return () => { logsRequest.current += 1; itemsRequest.current += 1; };
  }, [load]);

  const loadItems = useCallback(async (row) => {
    const request = ++itemsRequest.current;
    setSelectedSync(row);
    setItemsLoading(true);
    setSyncItems([]);
    setItemsError('');
    try {
      const resp = await fetchAdminIntegrationSyncItems(row.id);
      if (request !== itemsRequest.current) return;
      setSyncItems(resp?.data || []);
    } catch (error) {
      if (request !== itemsRequest.current) return;
      setItemsError('Failed to load sync items');
      toast.error('Failed to load sync items');
    } finally {
      if (request === itemsRequest.current) setItemsLoading(false);
    }
  }, []);

  const columns = useMemo(() => ([
    { key: 'created_at', label: 'Started', render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—') },
    { key: 'organization_name', label: 'Organization' },
    { key: 'integrator_name', label: 'Integrator' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'progress', label: 'Progress', render: (r) => <ProgressCell total={r.total_items ?? r.total ?? 0} processed={r.processed_items ?? r.processed ?? 0} /> },
    { key: 'last_error', label: 'Error', render: (r) => r.last_error || '—' },
    { key: 'actions', label: 'Actions', render: (r) => <InlineBtn type="button" onClick={() => loadItems(r)}>Items</InlineBtn> },
  ]), [loadItems]);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Sync Monitor</PageTitle></PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <AdminFilterBar>
          <Row>
            <Select aria-label="Sync status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="queued">queued</option>
              <option value="running">running</option>
              <option value="success">success</option>
              <option value="partial">partial</option>
              <option value="completed">completed</option>
              <option value="failed">failed</option>
            </Select>
            <Select aria-label="Sync period" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </Select>
            <FreshnessBadge status={loading ? 'running' : loadError ? 'failed' : 'success'} sourceType="operational" />
          </Row>
          <MainButton size="small" onClick={load}>Refresh</MainButton>
        </AdminFilterBar>

        {loadError ? <p role="alert">{loadError}</p> : <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No sync logs found" />}

        <PanelSection>
          <PanelHeader>
            <h3>Sync Items {selectedSync ? `for ${selectedSync.id}` : ''}</h3>
          </PanelHeader>
          {itemsError ? <p role="alert">{itemsError}</p> : <AdminDataTable
            loading={itemsLoading}
            rows={syncItems}
            emptyText={selectedSync ? 'No sync items for selected run' : 'Select a sync run to inspect items'}
            columns={[
              { key: 'entity_id', label: 'Entity ID' },
              { key: 'entity_type', label: 'Entity' },
              { key: 'status', label: 'Status' },
              { key: 'error_message', label: 'Error', render: (r) => r.error_message || '—' },
              { key: 'updated_at', label: 'Updated', render: (r) => (r.updated_at ? new Date(r.updated_at).toLocaleString() : '—') },
            ]}
          />}
        </PanelSection>
      </PageContent>
    </PageContainer>
  );
};

export default AdminSyncMonitor;

const ProgressCell = ({ total, processed }) => {
  const t = Number(total || 0);
  const p = Number(processed || 0);
  const pct = t > 0 ? Math.min(100, Math.round((p / t) * 100)) : 0;
  return (
    <ProgressWrap>
      <ProgressText>{p}/{t} ({pct}%)</ProgressText>
      <ProgressBar><ProgressFill style={{ width: `${pct}%` }} /></ProgressBar>
    </ProgressWrap>
  );
};

const Row = styled.div`display:flex;gap: 0.625rem;align-items:center;flex-wrap:wrap;`;
const Select = styled(SimpleSelect)`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.5625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const InlineBtn = styled.button`border:1px solid ${({theme})=>theme.colors.border};background:${({theme})=>theme.colors.surface};padding: 0.375rem 0.625rem;border-radius:8px;cursor:pointer;font-weight:600; &:hover{color:var(--color-primary);border-color:var(--color-primary);}`;
const ProgressWrap = styled.div`display:flex;flex-direction:column;gap: 0.25rem;min-width:160px;`;
const ProgressText = styled.span`font-size: 0.75rem;color:${({theme})=>theme.colors.textMuted};`;
const ProgressBar = styled.div`height:8px;background:${({theme})=>theme.colors.surfaceMuted};border-radius:999px;overflow:hidden;border:1px solid ${({theme})=>theme.colors.border};`;
const ProgressFill = styled.div`height:100%;background:var(--color-primary);`;
const PanelSection = styled.section`margin-top: 1.25rem;`;
const PanelHeader = styled.div`margin-bottom: 0.75rem; h3 { margin:0; color:${({theme})=>theme.colors.textPrimary}; font-size:1rem; }`;
